/**
 * LazyRouteBoundary
 *
 * Wraps lazy-loaded routes so that a failed dynamic import (e.g. stale Vite
 * chunk after a deploy, transient network blip, or corrupted optimized-deps
 * cache) renders a friendly "Retry / Reload" UI instead of a blank screen.
 *
 * Strategy:
 *  • One automatic silent retry after ~400 ms — handles the common "chunk
 *    just rotated" case without bothering the user.
 *  • If that also fails, show the fallback with a manual Reload button.
 *  • A hard reload (clearing module cache) is the most reliable recovery
 *    because Vite will then re-fetch the latest hashed chunks.
 */
import { Component, ErrorInfo, ReactNode, Suspense } from "react";
import { AlertTriangle, RefreshCw } from "lucide-react";

interface Props {
  children: ReactNode;
  /** Optional custom fallback shown while the lazy chunk loads. */
  loadingFallback?: ReactNode;
}

interface State {
  hasError: boolean;
  retried: boolean;
  error?: Error;
}

const isChunkLoadError = (err: unknown): boolean => {
  const msg = (err as Error)?.message?.toLowerCase() ?? "";
  return (
    msg.includes("importing a module script") ||
    msg.includes("failed to fetch dynamically imported module") ||
    msg.includes("loading chunk") ||
    msg.includes("loading css chunk") ||
    msg.includes("dynamically imported module")
  );
};

export class LazyRouteBoundary extends Component<Props, State> {
  state: State = { hasError: false, retried: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Silent auto-retry once for chunk-load errors — usually fixes a stale cache.
    if (!this.state.retried && isChunkLoadError(error)) {
      this.setState({ retried: true });
      setTimeout(() => this.setState({ hasError: false, error: undefined }), 400);
      return;
    }
    // eslint-disable-next-line no-console
    console.error("LazyRouteBoundary caught:", error, info);
  }

  handleReload = () => {
    // Hard reload clears in-memory module cache so Vite re-fetches latest chunks.
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-6">
          <div className="max-w-sm w-full text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <h2 className="text-lg font-bold text-foreground">Couldn't load this page</h2>
            <p className="text-sm text-muted-foreground">
              A network or cache issue prevented loading. Reloading usually fixes it.
            </p>
            <button
              onClick={this.handleReload}
              className="inline-flex items-center gap-2 px-4 h-11 rounded-lg bg-primary text-primary-foreground font-semibold hover:opacity-90 transition"
            >
              <RefreshCw className="w-4 h-4" /> Retry / Reload
            </button>
          </div>
        </div>
      );
    }

    return (
      <Suspense
        fallback={
          this.props.loadingFallback ?? (
            <div className="min-h-screen flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
            </div>
          )
        }
      >
        {this.props.children}
      </Suspense>
    );
  }
}

export default LazyRouteBoundary;
